import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY } from '../admin/security/AllowedIframeHostsSettings';
import QuillComponent from '../editor/QuillComponent';
import { withQuillContextProvider } from '../editor/QuillContext';
import useLinkShortcut from '../editor/useLinkShortcut';
import type {
  TemplateContent_OwnerFragment,
  TemplateContent_TemplatePostFragment,
  TemplateContent_TopicFragment,
} from '../graphql/generated/graphqlTypes';
import PostSkeleton from '../post/PostSkeleton';
import addFragments from '../utils/addFragments';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';
import styles from './TemplateContent.module.scss';
import TemplateHeader from './TemplateHeader';

const EDITOR_MODULE_CONFIG = {
  headerCollapse: true,
  hoveredBlotListener: true,
  prefixListener: true,
};

const TEMPLATE_CONTENT_TEMPLATE_POST_FRAGMENT = gql`
  fragment TemplateContent_templatePost on Post {
    id
    title
    content
    version
  }
`;

const TEMPLATE_CONTENT_OWNER_FRAGMENT = gql`
  fragment TemplateContent_owner on User {
    id
    ...TemplateHeader_owner
  }
  ${TemplateHeader.fragments.owner}
`;

const TEMPLATE_CONTENT_TOPIC_FRAGMENT = gql`
  fragment TemplateContent_topic on Topic {
    id
    ...TemplateHeader_topic
  }
  ${TemplateHeader.fragments.topic}
`;

interface TemplateContentProps {
  template: TemplateContent_TemplatePostFragment | null;
  topics: TemplateContent_TopicFragment[];
  owner: TemplateContent_OwnerFragment | null;
  loading: boolean;
  deleteTemplate: () => Promise<void>;
  onCreatePost?: () => void;
  onClose: () => void;
}

const BaseTemplateContent = ({
  template,
  topics,
  owner,
  loading,
  deleteTemplate,
  onCreatePost,
  onClose,
}: TemplateContentProps) => {
  const { data } = useQuery(ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY);
  const allowedEmbedHosts =
    data?.session?.organization?.settings?.allowedEmbedHosts || [];
  useLinkShortcut();

  const templateContent = template && !loading ? template.content : null;

  const navigate = useNavigate();
  const onEditTemplate = () => {
    if (template) {
      navigate(postPath(template), { state: { edit: true } });
      onClose();
    }
  };

  const onChangeLocation = () => {
    if (template) {
      navigate(postPath(template), { state: { edit: true, editTopic: true } });
      onClose();
    }
  };

  return (
    <>
      {template && !loading && (
        <TemplateHeader
          templateId={template.id}
          topics={topics}
          owner={owner}
          onEditTemplate={onEditTemplate}
          onChangeLocation={onChangeLocation}
          onDelete={deleteTemplate}
          onCreatePost={onCreatePost}
        />
      )}
      <div className={styles.editorContainer}>
        {templateContent ? (
          <div className={styles.editor}>
            <QuillComponent
              namespace="template"
              content={templateContent}
              titlePlaceholder="Untitled"
              readOnly
              moduleConfig={{
                ...EDITOR_MODULE_CONFIG,
                clipboard: { allowedEmbedHosts },
              }}
              rootClassName={styles.editorRoot}
            />
          </div>
        ) : (
          <div className={styles.loader}>
            <PostSkeleton />
          </div>
        )}
      </div>
    </>
  );
};

const TemplateContent = withQuillContextProvider(BaseTemplateContent);

export default addFragments(TemplateContent, {
  owner: TEMPLATE_CONTENT_OWNER_FRAGMENT,
  topic: TEMPLATE_CONTENT_TOPIC_FRAGMENT,
  templatePost: TEMPLATE_CONTENT_TEMPLATE_POST_FRAGMENT,
});
